/**
 * A refused assignment → the key that explains it.
 *
 * ── Match on the machine code, then on the status. Never on the message ──────────────────────
 * The same rule `saveErrorCopy.ts` follows: `title` and `detail` are human-readable text the server
 * may reword or localise at any time, so a client switching on them breaks on a typo fix. Only
 * `extensions.code` and the status are contract.
 *
 * ── What this endpoint actually emits, verified rather than assumed ──────────────────────────
 * `POST /plan-assignment` attaches **no code to most of its own refusals**. The out-of-bounds start
 * date, the unmatched override coordinate, the fork-not-a-library-entry guard and the empty template
 * are all `ValidationException`s constructed without one, so they arrive as a 400 with `code`
 * absent. The no-code fallback is therefore the COMMON path here, not the edge, and
 * `assignRequest.ts` exists so the trainer meets those four rules before the request rather than
 * after it.
 *
 * Its three CODED refusals all say the same thing — this client and the server disagree about the
 * plan's scheduling mode — and are collapsed onto one sentence below.
 *
 * Two more codes are reachable, and neither comes from the processor: the role gate in
 * `SupabaseJwtBearerConfigure` answers a trainer-only endpoint with `insufficient_role`, or with
 * `profile_missing` when the token is valid but no profile row exists. Both are 403s that mean "not
 * you", not "not this plan", and telling a trainer their plan is malformed when their session is
 * the problem sends them to fix the wrong thing.
 *
 * The 401 path is deliberately absent: the mutator refreshes and retries, and the session layer
 * owns what happens when that fails. A dialog wording its own token expiry would be a second
 * authority on the session.
 *
 * No React, no DOM, no query hook, no i18next: this is `model/`. It returns a KEY.
 */

/** The key a refusal resolves to when nothing more specific applies. */
export const UNKNOWN_ASSIGN_FAILURE_KEY = 'web:assign.failure.unknown';

/**
 * The refusal a client whose idea of the plan has gone stale gets. **One sentence for all three
 * codes, and it shows the code.**
 *
 * None of them is reachable from a correct client: `plan_self_paced_takes_no_schedule_input` and
 * `plan_self_paced_delivery_must_be_remote` fire when a start date, a time map, an override or a
 * non-Remote delivery is sent for a self-paced plan — and this flow omits all four for one
 * (`assignRequest.ts` § `selfPacedRequest`). `plan_schedule_input_required` is the mirror image: a
 * dated plan whose start date or delivery mode did not travel, which this flow always sends.
 *
 * So each of the three means the same thing operationally — the plan changed mode under an open
 * dialog, or this build disagrees with the server about it — and there is exactly one useful move,
 * which is to reopen the plan. Three bespoke sentences would be three pieces of copy nobody ever
 * reads, each explaining a scheduling input the trainer was never shown. The code rides along in the
 * text instead, so a bug report can name the door that shut.
 */
const STALE_SCHEDULE_INPUT_KEY = 'web:assign.failure.staleScheduleInput';

/**
 * The one refusal on this endpoint that NAMES something, and the reason the copy takes a value.
 *
 * `PlanAssignmentProcessor.EnsureNoActivePlanAsync` (`PlanAssignmentProcessor.cs:846`) throws a 409
 * carrying `client_already_has_active_plan` plus `activeAssignmentId` and `activePlanName`, because
 * R14 gives a client at most one active plan in any scheduling mode. It is the common refusal a
 * trainer will actually meet — reassigning to a client who is already training — and the generic 409
 * is worded for an optimistic-concurrency clash on the PLAN ("open it again and assign it once
 * more"), which would send them to reopen a plan that is perfectly fine.
 *
 * The plan's name is what makes the sentence actionable: the next move is to end that plan or to
 * wait, and neither is possible from a refusal that will not say which plan it means.
 * `activeAssignmentId` is deliberately unused — this app has no assigned-plan surface to link it to,
 * and an id shown to a trainer is a number they cannot act on.
 */
const ACTIVE_PLAN_KEY = 'web:assign.failure.activePlan';

/**
 * Backend code → key. Small on purpose: see the header for why the processor's own refusals are
 * not in it, and do not add a code here without finding the line that throws it.
 */
const ASSIGN_FAILURE_CODE_KEYS: Readonly<Record<string, string>> = {
  insufficient_role: 'web:assign.failure.role',
  // `ValidationErrorCodes.UnknownTimeZone`: the browser reported a zone id the server cannot
  // canonicalise, and the assignment's hours travel with one (ADR 2026-09-08). The write retries
  // itself once without the zone before this sentence can be reached — see
  // `shared/lib/occurrenceZone` — so what it words is the case where the retry was refused too,
  // and it names the browser's clock because that is the only thing the trainer can go and fix.
  unknown_time_zone: 'web:assign.failure.unknownTimeZone',
  profile_missing: 'web:assign.failure.profile',
  plan_self_paced_takes_no_schedule_input: STALE_SCHEDULE_INPUT_KEY,
  plan_self_paced_delivery_must_be_remote: STALE_SCHEDULE_INPUT_KEY,
  plan_schedule_input_required: STALE_SCHEDULE_INPUT_KEY,
  client_already_has_active_plan: ACTIVE_PLAN_KEY,
};

/** Status → key, for the refusals that carry no code. */
const ASSIGN_FAILURE_STATUS_KEYS: Readonly<Record<number, string>> = {
  // The customer is not on this trainer's roster, or the template is gone. Collapsed to 404 on
  // purpose server-side, so the copy must cover both without claiming to know which.
  404: 'web:assign.failure.notFound',
  403: 'web:assign.failure.forbidden',
  400: 'web:assign.failure.rejected',
  409: 'web:assign.failure.conflict',
};

/**
 * The i18n key for a refused assignment. **Always a key** — an unknown code and an unknown status
 * both resolve to the generic one, so no caller carries half of this contract.
 *
 * `Object.hasOwn` before indexing: a bare lookup walks the prototype chain, so a backend code of
 * `"constructor"` would answer with a function.
 */
export function assignFailureKey(status: number, code: string | null | undefined): string {
  if (code && Object.hasOwn(ASSIGN_FAILURE_CODE_KEYS, code)) {
    return ASSIGN_FAILURE_CODE_KEYS[code] ?? UNKNOWN_ASSIGN_FAILURE_KEY;
  }
  if (Object.hasOwn(ASSIGN_FAILURE_STATUS_KEYS, status)) {
    return ASSIGN_FAILURE_STATUS_KEYS[status] ?? UNKNOWN_ASSIGN_FAILURE_KEY;
  }
  return UNKNOWN_ASSIGN_FAILURE_KEY;
}

/** The key AND everything it interpolates — the whole of what the dialog needs to word a refusal. */
export interface AssignFailureCopy {
  readonly key: string;
  /** Values for the key's placeholders. Every key's placeholders are supplied, always. */
  readonly values: Readonly<Record<string, string>>;
}

/**
 * A named string extension off a problem body, or `null` — absent, wrong-typed and blank all answer
 * the same, because all three leave the sentence with nothing to name.
 *
 * The body arrives as `unknown` rather than as `ProblemDetails`, and that is the honest type:
 * `ProblemDetails` declares the RFC's own members, ASP.NET flattens an exception's extensions
 * alongside them, so `activePlanName` is genuinely on the wire and genuinely not in the type. The
 * alternative is an index signature on the shared `ProblemDetails`, which would silence the typo
 * check on `code` and `detail` for every consumer in the app to serve one refusal here.
 */
function extensionText(problem: unknown, name: string): string | null {
  if (typeof problem !== 'object' || problem === null) return null;
  const value = (problem as Record<string, unknown>)[name];
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

/**
 * The refused assignment, as the sentence the dialog renders — key plus values, decided together.
 *
 * The two are one decision rather than two calls a hook combines: a key whose placeholder is not
 * supplied renders `{{plan}}` at the trainer, and the only place that pairing can be tested is here.
 *
 * **A coded refusal that arrives without the value its sentence needs falls back rather than
 * rendering a hole.** `client_already_has_active_plan` promises `activePlanName`; a proxy that
 * strips extensions, or a future backend that stops sending it, would otherwise produce "Ten klient
 * ma już aktywny plan: . Zakończ go…". The generic 409 is a worse sentence and a true one.
 */
export function assignFailureCopy(
  status: number,
  code: string | null | undefined,
  /** The RFC 9457 body as it arrived, extensions and all. See `extensionText` for why `unknown`. */
  problem: unknown,
): AssignFailureCopy {
  const key = assignFailureKey(status, code);

  if (key === ACTIVE_PLAN_KEY) {
    const plan = extensionText(problem, 'activePlanName');
    return plan === null
      ? { key: ASSIGN_FAILURE_STATUS_KEYS[409] ?? UNKNOWN_ASSIGN_FAILURE_KEY, values: {} }
      : { key, values: { plan } };
  }

  // The code rides along as a value for the one sentence that shows it — the scheduling-input
  // refusals, which are unreachable from a correct client and therefore worth naming in a bug
  // report. Every other sentence ignores it, which is what i18next does with a spare value.
  return { key, values: { code: code ?? '' } };
}
